
import React from 'react'
import {
  DialogRoot as Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface CanvasModalsProps {
  // Rename dialog
  showRenameDialog: boolean
  setShowRenameDialog: (show: boolean) => void
  newCanvasName: string
  setNewCanvasName: (name: string) => void
  onRenameConfirm: () => Promise<void>

  // Delete dialog
  showDeleteDialog: boolean
  setShowDeleteDialog: (show: boolean) => void
  canvasTitle: string
  onDeleteConfirm: () => Promise<void>

  // Duplicate dialog
  showDuplicateDialog: boolean
  setShowDuplicateDialog: (show: boolean) => void
  onDuplicateConfirm: () => Promise<void>

  // New Session dialog
  showNewCanvasDialog: boolean
  setShowNewCanvasDialog: (show: boolean) => void
  onNewCanvasConfirm: () => void
}

/**
 * Canvas modals component
 * Handles all modal dialogs for canvas page (rename, delete, duplicate, new canvas)
 */
export function CanvasModals({
  showRenameDialog,
  setShowRenameDialog,
  newCanvasName,
  setNewCanvasName,
  onRenameConfirm,
  showDeleteDialog,
  setShowDeleteDialog,
  canvasTitle,
  onDeleteConfirm,
  showDuplicateDialog,
  setShowDuplicateDialog,
  onDuplicateConfirm,
  showNewCanvasDialog,
  setShowNewCanvasDialog,
  onNewCanvasConfirm,
}: CanvasModalsProps) {
  return (
    <>
      {/* Rename Session Dialog */}
      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Session</DialogTitle>
            <DialogDescription>
              Enter a new name for your session.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="canvas-name">Session Name</Label>
              <Input
                id="canvas-name"
                value={newCanvasName}
                onChange={(e) => setNewCanvasName(e.target.value)}
                placeholder="Enter session name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onRenameConfirm()
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRenameDialog(false)}>
              Cancel
            </Button>
            <Button onClick={onRenameConfirm} disabled={!newCanvasName.trim()}>
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Session Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Session</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{canvasTitle}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={onDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Session Confirmation Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate Session</DialogTitle>
            <DialogDescription>
              Create a copy of this session?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={onDuplicateConfirm}>
              Duplicate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Session Confirmation Dialog */}
      <Dialog open={showNewCanvasDialog} onOpenChange={setShowNewCanvasDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Session</DialogTitle>
            <DialogDescription>
              Create a new session?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCanvasDialog(false)}>
              Cancel
            </Button>
            <Button onClick={onNewCanvasConfirm}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
